import {clamp, Nullable, Option, Terminable, Terminator, unitValue} from "@opendaw/lib-std"
import {Address} from "@opendaw/lib-box"
import {AnimationFrame} from "@opendaw/lib-dom"
import {EngineAddresses} from "@opendaw/studio-adapters"
import {Project} from "@opendaw/studio-core"

const Vertex = `#version 300 es
void main() {
    vec2 corner = vec2((gl_VertexID << 1) & 2, gl_VertexID & 2);
    gl_Position = vec4(corner * 2.0 - 1.0, 0.0, 1.0);
}`

// Texture rows are flipped on upload, so uv.y == 0 is the bottom of the screen, where the head switches.
const Fragment = `#version 300 es
precision highp float;
uniform sampler2D uTape;
uniform vec2 uResolution;
uniform float uTime;
uniform float uSeed;
uniform float uDrums;
uniform float uKick;
uniform float uBass;
uniform float uMaster;
uniform vec3 uCube[8];
uniform sampler2D uSmiley;
uniform vec4 uSprites[8];
out vec4 outColor;

const vec3 BACKGROUND = vec3(0.082, 0.094, 0.098);
const vec3 WIRE = vec3(0.26, 0.42, 0.17);
const ivec2 EDGES[12] = ivec2[12](
    ivec2(0, 1), ivec2(2, 3), ivec2(4, 5), ivec2(6, 7),
    ivec2(0, 2), ivec2(1, 3), ivec2(4, 6), ivec2(5, 7),
    ivec2(0, 4), ivec2(1, 5), ivec2(2, 6), ivec2(3, 7));

float hash(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
}

float wander(float x) {
    float i = floor(x);
    return mix(hash(vec2(i, uSeed)), hash(vec2(i + 1.0, uSeed)), smoothstep(0.0, 1.0, fract(x)));
}

float segment(vec2 p, vec2 a, vec2 b, out float t) {
    vec2 ab = b - a;
    t = clamp(dot(p - a, ab) / max(dot(ab, ab), 1e-4), 0.0, 1.0);
    return length(p - a - ab * t);
}

// A wireframe cube behind the face. The corners arrive projected in pixels with their depth in z, so this only
// draws the twelve edges: a thin core, a glow that swells with the 303, and the far edges fading into the dark.
vec3 cube(vec2 uv) {
    vec2 p = uv * uResolution;
    float width = uResolution.y * 0.0012 * (1.0 + 0.6 * uMaster);
    vec3 color = vec3(0.0);
    for (int i = 0; i < 12; i++) {
        vec3 a = uCube[EDGES[i].x];
        vec3 b = uCube[EDGES[i].y];
        float t;
        float d = segment(p, a.xy, b.xy, t);
        float depth = mix(a.z, b.z, t);
        float near = 0.15 + 0.85 * (0.5 - 0.5 * depth);
        float core = smoothstep(width, width * 0.3, d);
        float glow = exp(-d / (width * 5.0)) * (0.05 + 0.3 * uBass);
        color += WIRE * near * (core + glow);
    }
    return color;
}

// A flock of tiny smileys drifting through the room. Each arrives as centre and size in pixels plus an angle,
// the smaller ones sit further back and are dimmer. Always sampled, masked by the sprite's box, so the mipmap
// selection stays in uniform control flow.
vec3 flock(vec3 color, vec2 uv) {
    vec2 p = uv * uResolution;
    for (int i = 0; i < 8; i++) {
        vec4 s = uSprites[i];
        vec2 d = p - s.xy;
        float c = cos(s.w), n = sin(s.w);
        vec2 local = vec2(d.x * c + d.y * n, d.y * c - d.x * n) / s.z;
        float inside = step(max(abs(local.x), abs(local.y)), 0.5);
        vec4 tex = texture(uSmiley, local + 0.5);
        float dim = clamp(s.z / uResolution.y * 6.0, 0.25, 0.55);
        color = mix(color, tex.rgb * dim, tex.a * inside);
    }
    return color;
}

// The tape is the face on a transparent sheet. Everything behind it is drawn here: the dark room, the flock and
// the cube. The face alone takes the extra twitch, so its edges shiver while the room behind it stays put.
vec3 tap(vec2 uv, float twitch) {
    uv = clamp(uv, 0.0, 1.0);
    vec4 face = texture(uTape, clamp(uv + vec2(twitch, 0.0), 0.0, 1.0));
    return mix(flock(BACKGROUND, uv) + cube(uv), face.rgb, face.a);
}

vec3 hue(vec3 color, float angle) {
    const vec3 k = vec3(0.57735);
    float c = cos(angle), s = sin(angle);
    return color * c + cross(k, color) * s + k * dot(k, color) * (1.0 - c);
}

void main() {
    vec2 uv = gl_FragCoord.xy / uResolution;
    float t = uTime;
    float line = floor(uv.y * uResolution.y);
    // The whole picture rides up and down: a slow breathing plus a jolt on every kick.
    uv.y += (wander(t * 1.5) - 0.5) * 0.001 + (wander(t * 50.0) - 0.5) * 0.005 * uKick;
    // Slow horizontal error that neighbouring lines share, so it bends the picture instead of shredding it.
    float wobble = sin(uv.y * 30.0 + t * 3.0) * 0.0002
        + (wander(uv.y * 18.0 + t * 4.0) - 0.5) * 0.0007 * (0.4 + uMaster)
        + (wander(uv.y * 90.0 + t * 12.0 + 31.0) - 0.5) * 0.0002;
    // A tracking band drifts slowly down the screen and tears the lines it crosses, worst when the drums hit.
    // The tear is two layers of noise at different speeds and scales, so the ripples stay restless.
    float bandY = 1.0 - fract(t * 0.15 + uSeed);
    float band = smoothstep(0.05, 0.0, abs(uv.y - bandY));
    float tearing = step(0.3, wander(t * 9.0 + 7.0));
    float ripple = (wander(uv.y * 200.0 + t * 30.0 + 57.0) - 0.5)
        + (wander(uv.y * 600.0 - t * 70.0 + 13.0) - 0.5) * 0.5;
    wobble += band * tearing * (0.0025 + 0.012 * uDrums) * (ripple - 0.15);
    // Head switching at the bottom edge: the last lines are torn sideways and full of noise.
    float head = smoothstep(0.035, 0.0, uv.y);
    wobble += head * 0.01 * (hash(vec2(line, floor(t * 90.0))) - 0.5);
    uv.x += wobble;
    // Tiny distortion all over the face: fast fine ripples and a per-line twitch that never rest, more on loud parts.
    float twitch = (wander(uv.y * 400.0 + t * 40.0 + 71.0) - 0.5) * 0.0012 * (0.6 + 0.4 * uMaster)
        + (hash(vec2(line, floor(t * 60.0))) - 0.5) * 0.0006;
    // Chroma comes apart: three planes, three offsets, red and blue smeared sideways like tape chroma.
    float split = 0.001 + 0.003 * uBass + 0.002 * uKick;
    vec2 dr = vec2(split, 0.0008);
    vec2 dg = vec2(-split * 0.3, -0.0006);
    vec2 db = vec2(-split * 1.2, 0.0003);
    vec2 smear = vec2(0.002, 0.0);
    float r = (tap(uv + dr, twitch).r + tap(uv + dr + smear, twitch).r + tap(uv + dr - smear, twitch).r) / 3.0;
    float g = tap(uv + dg, twitch).g;
    float b = (tap(uv + db, twitch).b + tap(uv + db + smear * 1.3, twitch).b
        + tap(uv + db - smear * 1.3, twitch).b) / 3.0;
    vec3 color = vec3(r, g, b);
    // Washed tape colour, the 303 pushes the hue around and saturates it back.
    float luma = dot(color, vec3(0.299, 0.587, 0.114));
    color = mix(vec3(luma), color, 0.75 + 0.3 * uBass);
    color = hue(color, (wander(t * 0.3) - 0.5) * 0.25 + uBass * 0.35);
    color = color * vec3(1.03, 0.97, 1.06) + vec3(0.025, 0.012, 0.035);
    // Scanlines, grain, dropouts.
    color *= 0.95 + 0.05 * step(0.5, fract(gl_FragCoord.y * 0.5));
    float grain = hash(gl_FragCoord.xy + fract(t) * 1000.0) - 0.5;
    color += grain * (0.06 + 0.16 * uMaster + 0.2 * head + 0.1 * band * tearing);
    float dropout = step(0.9985 - 0.006 * uDrums, hash(vec2(line * 0.37 + 1.0, floor(t * 20.0))));
    color += dropout * hash(vec2(line, floor(t * 20.0))) * 0.2;
    color = mix(color, vec3(hash(vec2(gl_FragCoord.x * 0.1 + t * 300.0, line))), head * 0.6);
    // Vignette and a light pump on the kick.
    vec2 centered = uv - 0.5;
    color *= 1.0 - dot(centered, centered) * 0.7;
    color *= 1.0 + 0.12 * uKick;
    outColor = vec4(color, 1.0);
}`

type Levels = {drums: unitValue, kick: unitValue, bass: unitValue, master: unitValue}
// Positions and velocities in screen fractions, size as a fraction of the short edge, angles in radians.
type Sprite = {x: number, y: number, vx: number, vy: number, size: number, angle: number, spin: number}

// The picture is a 2D canvas the size of the screen with the face drawn where the DOM lays it out. The GL pass
// treats the whole screen as tape, so nothing is cropped when the effects push pixels around.
export class Vhs implements Terminable {
    static readonly MaxPixelRatio = 2.0
    static readonly Diagonal = Math.sqrt(3.0)
    static readonly Sprites = 8
    static readonly SmileySize = 256

    static create(canvas: HTMLCanvasElement, source: HTMLImageElement, anchor: HTMLElement): Option<Vhs> {
        const gl = canvas.getContext("webgl2", {antialias: false, alpha: false, premultipliedAlpha: false})
        return gl === null ? Option.None : Option.wrap(new Vhs(canvas, gl, source, anchor))
    }

    readonly #terminator = new Terminator()
    readonly #canvas: HTMLCanvasElement
    readonly #gl: WebGL2RenderingContext
    readonly #source: HTMLImageElement
    readonly #anchor: HTMLElement
    readonly #tape: HTMLCanvasElement
    readonly #texture: WebGLTexture
    readonly #smiley: WebGLTexture
    readonly #uniforms: Record<keyof Levels | "time" | "seed" | "resolution" | "cube" | "sprites",
        Nullable<WebGLUniformLocation>>
    readonly #seed: number
    readonly #peaks = {drums: 0.0, bass: 0.0, master: 0.0}
    readonly #levels: Levels = {drums: 0.0, kick: 0.0, bass: 0.0, master: 0.0}
    readonly #last = {drums: 0.0}
    readonly #spin = {x: 0.0, y: 0.0, z: 0.0}
    readonly #corners = new Float32Array(8 * 3)
    readonly #sprites: ReadonlyArray<Sprite> = Array.from({length: Vhs.Sprites}, () => Vhs.#spawn(Math.random()))
    readonly #flock = new Float32Array(Vhs.Sprites * 4)
    #clock: number = performance.now()

    private constructor(canvas: HTMLCanvasElement, gl: WebGL2RenderingContext, source: HTMLImageElement,
                        anchor: HTMLElement) {
        this.#canvas = canvas
        this.#gl = gl
        this.#source = source
        this.#anchor = anchor
        this.#tape = document.createElement("canvas")
        this.#seed = Math.random() * 100.0
        const program = this.#link()
        gl.useProgram(program)
        this.#texture = gl.createTexture()
        gl.bindTexture(gl.TEXTURE_2D, this.#texture)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true)
        this.#smiley = gl.createTexture()
        gl.activeTexture(gl.TEXTURE1)
        gl.bindTexture(gl.TEXTURE_2D, this.#smiley)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
        // Transparent until the smiley has loaded, an incomplete texture would sample as opaque black.
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(4))
        gl.activeTexture(gl.TEXTURE0)
        this.#uniforms = {
            drums: gl.getUniformLocation(program, "uDrums"),
            kick: gl.getUniformLocation(program, "uKick"),
            bass: gl.getUniformLocation(program, "uBass"),
            master: gl.getUniformLocation(program, "uMaster"),
            time: gl.getUniformLocation(program, "uTime"),
            seed: gl.getUniformLocation(program, "uSeed"),
            resolution: gl.getUniformLocation(program, "uResolution"),
            cube: gl.getUniformLocation(program, "uCube"),
            sprites: gl.getUniformLocation(program, "uSprites")
        }
        gl.uniform1i(gl.getUniformLocation(program, "uTape"), 0)
        gl.uniform1i(gl.getUniformLocation(program, "uSmiley"), 1)
        const observer = new ResizeObserver(() => this.#layout())
        observer.observe(document.body)
        observer.observe(anchor)
        this.#terminator.own({terminate: () => observer.disconnect()})
        this.#terminator.own(AnimationFrame.add(() => this.#frame()))
        const ready = () => {
            this.#stamp()
            this.#layout()
        }
        if (source.complete) {ready()} else {source.addEventListener("load", ready, {once: true})}
    }

    // Peaks from the engine's live stream: the two units by their audio-unit address, the master by its own.
    listen(project: Project, drums: Address, bass: Address): Terminable {
        const {liveStreamReceiver} = project
        return Terminable.many(
            liveStreamReceiver.subscribeFloats(drums, values => this.#peaks.drums = Math.max(values[0], values[1])),
            liveStreamReceiver.subscribeFloats(bass, values => this.#peaks.bass = Math.max(values[0], values[1])),
            liveStreamReceiver.subscribeFloats(EngineAddresses.PEAKS,
                values => this.#peaks.master = Math.max(values[2], values[3])),
            {terminate: () => Object.assign(this.#peaks, {drums: 0.0, bass: 0.0, master: 0.0})}
        )
    }

    terminate(): void {this.#terminator.terminate()}

    #frame(): void {
        const now = performance.now()
        const dt = Math.min((now - this.#clock) / 1000.0, 0.1)
        this.#clock = now
        this.#follow()
        this.#turn(dt)
        this.#fly(dt)
        const gl = this.#gl
        const {drums, kick, bass, master} = this.#levels
        gl.uniform1f(this.#uniforms.time, now / 1000.0)
        gl.uniform3fv(this.#uniforms.cube, this.#corners)
        gl.uniform4fv(this.#uniforms.sprites, this.#flock)
        gl.uniform1f(this.#uniforms.drums, drums)
        gl.uniform1f(this.#uniforms.kick, kick)
        gl.uniform1f(this.#uniforms.bass, bass)
        gl.uniform1f(this.#uniforms.master, master)
        gl.drawArrays(gl.TRIANGLES, 0, 3)
    }

    // Envelope followers. The units sit well below 0 dBFS, so the peaks are lifted into 0..1 with a soft knee.
    #follow(): void {
        const lift = (peak: number, gain: number): unitValue => clamp(Math.sqrt(peak * gain), 0.0, 1.0)
        const drums = lift(this.#peaks.drums, 3.0)
        const bass = lift(this.#peaks.bass, 4.0)
        const master = lift(this.#peaks.master, 4.0)
        const onset = clamp((this.#peaks.drums - this.#last.drums) * 12.0, 0.0, 1.0)
        this.#last.drums = this.#peaks.drums
        const levels = this.#levels
        levels.drums += (drums - levels.drums) * (drums > levels.drums ? 0.7 : 0.15)
        levels.bass += (bass - levels.bass) * (bass > levels.bass ? 0.12 : 0.03)
        levels.master += (master - levels.master) * 0.2
        levels.kick = Math.max(onset, levels.kick * 0.8)
    }

    // The cube turns on its own and speeds up with the drums, swells on every kick, and lands projected in pixels
    // with a depth in -1..1 so the shader can fade the far edges.
    #turn(dt: number): void {
        const {drums, kick} = this.#levels
        const spin = this.#spin
        spin.x += dt * (0.35 + drums * 1.5)
        spin.y += dt * (0.55 + drums * 2.5)
        spin.z += dt * (0.15 + drums * 0.5)
        const cx = Math.cos(spin.x), sx = Math.sin(spin.x)
        const cy = Math.cos(spin.y), sy = Math.sin(spin.y)
        const cz = Math.cos(spin.z), sz = Math.sin(spin.z)
        const width = this.#canvas.width, height = this.#canvas.height
        const size = Math.min(width, height) * (0.29 + 0.05 * kick)
        const corners = this.#corners
        for (let i = 0; i < 8; i++) {
            let x = (i & 1) === 0 ? -1.0 : 1.0
            let y = (i & 2) === 0 ? -1.0 : 1.0
            let z = (i & 4) === 0 ? -1.0 : 1.0
            const y1 = y * cx - z * sx, z1 = y * sx + z * cx
            const x1 = x * cy + z1 * sy
            z = -x * sy + z1 * cy
            x = x1 * cz - y1 * sz
            y = x1 * sz + y1 * cz
            const perspective = 3.0 / (3.6 + z)
            corners[i * 3] = width * 0.5 + x * perspective * size
            corners[i * 3 + 1] = height * 0.5 + y * perspective * size
            corners[i * 3 + 2] = z / Vhs.Diagonal
        }
    }

    // The flock drifts across the room, faster and spinning harder with the drums, puffing up on every kick.
    // Whoever leaves the screen comes back in from the opposite side.
    #fly(dt: number): void {
        const {drums, kick} = this.#levels
        const width = this.#canvas.width, height = this.#canvas.height
        const short = Math.min(width, height)
        const pace = 0.4 + drums * 1.6
        const flock = this.#flock
        this.#sprites.forEach((sprite, index) => {
            sprite.x += sprite.vx * dt * pace
            sprite.y += sprite.vy * dt * pace
            sprite.angle += sprite.spin * dt * (1.0 + drums * 3.0)
            const margin = sprite.size * 1.5
            if (sprite.x < -margin) {sprite.x += 1.0 + margin * 2.0}
            if (sprite.x > 1.0 + margin) {sprite.x -= 1.0 + margin * 2.0}
            if (sprite.y < -margin) {sprite.y += 1.0 + margin * 2.0}
            if (sprite.y > 1.0 + margin) {sprite.y -= 1.0 + margin * 2.0}
            flock[index * 4] = sprite.x * width
            flock[index * 4 + 1] = sprite.y * height
            flock[index * 4 + 2] = sprite.size * short * (1.0 + 0.25 * kick)
            flock[index * 4 + 3] = sprite.angle
        })
    }

    // A sprite somewhere on screen, heading in a random direction at a random pace. Small ones move slower.
    static #spawn(seed: number): Sprite {
        const heading = seed * Math.PI * 2.0
        const size = 0.025 + Math.random() * 0.04
        const speed = (0.04 + Math.random() * 0.08) * (size / 0.045)
        return {
            x: Math.random(), y: Math.random(),
            vx: Math.cos(heading) * speed, vy: Math.sin(heading) * speed,
            size, angle: Math.random() * Math.PI * 2.0,
            spin: (Math.random() - 0.5) * 3.0
        }
    }

    // The smiley once, small, with mipmaps so it survives being shrunk to a speck.
    #stamp(): void {
        const size = Vhs.SmileySize
        const canvas = document.createElement("canvas")
        canvas.width = size
        canvas.height = size
        const context = canvas.getContext("2d")
        if (context === null || !this.#source.complete || this.#source.naturalWidth === 0) {return}
        context.drawImage(this.#source, 0, 0, size, size)
        const gl = this.#gl
        gl.activeTexture(gl.TEXTURE1)
        gl.bindTexture(gl.TEXTURE_2D, this.#smiley)
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas)
        gl.generateMipmap(gl.TEXTURE_2D)
        gl.activeTexture(gl.TEXTURE0)
    }

    // The face lands exactly where the DOM element sits, so the buttons below still line up with it.
    #layout(): void {
        const ratio = Math.min(window.devicePixelRatio, Vhs.MaxPixelRatio)
        const width = Math.max(1, Math.round(window.innerWidth * ratio))
        const height = Math.max(1, Math.round(window.innerHeight * ratio))
        this.#canvas.width = width
        this.#canvas.height = height
        this.#tape.width = width
        this.#tape.height = height
        const context = this.#tape.getContext("2d")
        if (context === null) {return}
        context.clearRect(0, 0, width, height)
        const rect = this.#anchor.getBoundingClientRect()
        if (this.#source.complete && this.#source.naturalWidth > 0) {
            context.drawImage(this.#source, rect.left * ratio, rect.top * ratio, rect.width * ratio, rect.height * ratio)
        }
        const gl = this.#gl
        gl.viewport(0, 0, width, height)
        gl.bindTexture(gl.TEXTURE_2D, this.#texture)
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.#tape)
        gl.uniform2f(this.#uniforms.resolution, width, height)
        gl.uniform1f(this.#uniforms.seed, this.#seed)
    }

    #link(): WebGLProgram {
        const gl = this.#gl
        const compile = (type: GLenum, code: string): WebGLShader => {
            const shader = gl.createShader(type)
            if (shader === null) {throw new Error("Could not create shader")}
            gl.shaderSource(shader, code)
            gl.compileShader(shader)
            if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
                throw new Error(gl.getShaderInfoLog(shader) ?? "Shader did not compile")
            }
            return shader
        }
        const program = gl.createProgram()
        gl.attachShader(program, compile(gl.VERTEX_SHADER, Vertex))
        gl.attachShader(program, compile(gl.FRAGMENT_SHADER, Fragment))
        gl.linkProgram(program)
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            throw new Error(gl.getProgramInfoLog(program) ?? "Program did not link")
        }
        return program
    }
}
